import { useEffect, useMemo, useState } from "react";
import api from "@/lib/apiClient";
import { PageHeader, SectionCard, Loader, EmptyState } from "@/components/common";
import { formatINR, formatDateTime, timeAgo } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Minus, Trash2, Search, ShoppingCart, Receipt, Loader2, Gem, Printer } from "lucide-react";
import { toast } from "sonner";

export default function Billing() {
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [invoices, setInvoices] = useState(null);
  const [cart, setCart] = useState([]);
  const [q, setQ] = useState("");
  const [customerId, setCustomerId] = useState("walkin");
  const [method, setMethod] = useState("cash");
  const [tax, setTax] = useState(0);
  const [saving, setSaving] = useState(false);
  const [viewInvoice, setViewInvoice] = useState(null);

  const load = () => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => setProducts([]));
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => setCustomers([]));
    api.get("/invoices").then((r) => setInvoices(r.data)).catch(() => setInvoices([]));
  };
  useEffect(() => { load(); }, []);

  const addToCart = (p) => {
    setCart((c) => {
      const ex = c.find((i) => i.product_id === p.id);
      if (ex) return c.map((i) => i.product_id === p.id ? { ...i, quantity: Math.min(i.quantity + 1, p.quantity) } : i);
      return [...c, { product_id: p.id, name: p.name, price: p.selling_price, quantity: 1, discount: 0, max: p.quantity }];
    });
  };
  const updateQty = (id, d) => setCart((c) => c.map((i) => i.product_id === id ? { ...i, quantity: Math.max(1, Math.min(i.quantity + d, i.max)) } : i));
  const setDisc = (id, v) => setCart((c) => c.map((i) => i.product_id === id ? { ...i, discount: +v || 0 } : i));
  const removeItem = (id) => setCart((c) => c.filter((i) => i.product_id !== id));

  const subtotal = useMemo(() => cart.reduce((s, i) => s + i.price * i.quantity, 0), [cart]);
  const discountTotal = useMemo(() => cart.reduce((s, i) => s + i.discount, 0), [cart]);
  const taxable = subtotal - discountTotal;
  const taxAmt = Math.round(taxable * (+tax) / 100);
  const total = taxable + taxAmt;

  const filtered = products.filter((p) => p.quantity > 0 && `${p.name} ${p.sku}`.toLowerCase().includes(q.toLowerCase()));

  const checkout = async () => {
    if (!cart.length) return toast.error("Add items to the bill first");
    setSaving(true);
    try {
      const res = await api.post("/invoices", {
        customer_id: customerId === "walkin" ? null : customerId,
        customer_name: customerId === "walkin" ? "Walk-in Customer" : customers.find((c) => c.id === customerId)?.name,
        items: cart.map((i) => ({ product_id: i.product_id, name: i.name, quantity: i.quantity, price: i.price, discount: i.discount })),
        payment_method: method, tax_percent: +tax,
      });
      toast.success(`Invoice ${res.data.invoice_number} created · Stock updated`);
      setViewInvoice(res.data);
      setCart([]); setCustomerId("walkin"); setMethod("cash"); setTax(0);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
    } finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Sales & Billing" subtitle="Create bills, take payments, and auto-update inventory & revenue." />

      <Tabs defaultValue="new">
        <TabsList className="mb-5">
          <TabsTrigger value="new" data-testid="tab-new-bill"><ShoppingCart className="h-4 w-4 mr-1.5" /> New Bill</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-invoices"><Receipt className="h-4 w-4 mr-1.5" /> Invoices</TabsTrigger>
        </TabsList>

        <TabsContent value="new">
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Product picker */}
            <SectionCard className="lg:col-span-2" title="Products" testid="billing-products">
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input data-testid="billing-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products to add…" className="pl-9" />
              </div>
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-2.5 max-h-[520px] overflow-y-auto pr-1">
                {filtered.map((p) => (
                  <button key={p.id} data-testid={`add-cart-${p.id}`} onClick={() => addToCart(p)}
                    className="text-left rounded-lg border border-border p-3 hover:border-primary/50 hover:bg-primary/5 transition-colors">
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-medium text-sm leading-tight">{p.name}</div>
                      {p.industry === "jewellery" && <Gem className="h-3.5 w-3.5 text-primary shrink-0" />}
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-sm font-semibold text-primary">{formatINR(p.selling_price, { compact: true })}</span>
                      <span className="text-xs text-muted-foreground">{p.quantity} left</span>
                    </div>
                  </button>
                ))}
                {filtered.length === 0 && <div className="col-span-full"><EmptyState icon={Search} title="No products match" /></div>}
              </div>
            </SectionCard>

            {/* Cart */}
            <SectionCard title="Current Bill" testid="billing-cart">
              {cart.length === 0 ? <EmptyState icon={ShoppingCart} title="Bill is empty" subtitle="Click products to add" /> : (
                <div className="space-y-3">
                  {cart.map((i) => (
                    <div key={i.product_id} className="rounded-lg border border-border p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-medium text-sm">{i.name}</div>
                        <button onClick={() => removeItem(i.product_id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-1.5">
                          <button onClick={() => updateQty(i.product_id, -1)} className="grid place-items-center h-7 w-7 rounded border border-border hover:bg-secondary"><Minus className="h-3.5 w-3.5" /></button>
                          <span className="w-8 text-center text-sm font-semibold">{i.quantity}</span>
                          <button onClick={() => updateQty(i.product_id, 1)} className="grid place-items-center h-7 w-7 rounded border border-border hover:bg-secondary"><Plus className="h-3.5 w-3.5" /></button>
                        </div>
                        <span className="text-sm font-semibold">{formatINR(i.price * i.quantity)}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <Label className="text-xs text-muted-foreground">Discount ₹</Label>
                        <Input type="number" value={i.discount} onChange={(e) => setDisc(i.product_id, e.target.value)} className="h-7 text-xs" />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-4 space-y-3 border-t border-border pt-4">
                <div>
                  <Label className="text-xs">Customer</Label>
                  <Select value={customerId} onValueChange={setCustomerId}>
                    <SelectTrigger data-testid="billing-customer" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="walkin">Walk-in Customer</SelectItem>
                      {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Payment</Label>
                    <Select value={method} onValueChange={setMethod}>
                      <SelectTrigger data-testid="billing-payment" className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{["cash", "upi", "card", "bank", "credit"].map((m) => <SelectItem key={m} value={m} className="uppercase">{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">GST %</Label><Input type="number" value={tax} onChange={(e) => setTax(e.target.value)} className="mt-1" /></div>
                </div>

                <div className="space-y-1 text-sm pt-1">
                  <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span>{formatINR(subtotal)}</span></div>
                  <div className="flex justify-between text-muted-foreground"><span>Discount</span><span>-{formatINR(discountTotal)}</span></div>
                  <div className="flex justify-between text-muted-foreground"><span>Tax</span><span>{formatINR(taxAmt)}</span></div>
                  <div className="flex justify-between font-heading text-lg font-bold pt-1 border-t border-border mt-1"><span>Total</span><span className="text-primary">{formatINR(total)}</span></div>
                </div>

                <Button onClick={checkout} disabled={saving || !cart.length} data-testid="checkout-btn" className="w-full font-semibold">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : `Generate Invoice · ${formatINR(total)}`}
                </Button>
              </div>
            </SectionCard>
          </div>
        </TabsContent>

        <TabsContent value="history">
          <SectionCard testid="invoices-table">
            {!invoices ? <Loader /> : invoices.length === 0 ? <EmptyState icon={Receipt} title="No invoices yet" /> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                    <th className="text-left font-semibold py-3">Invoice</th>
                    <th className="text-left font-semibold py-3">Customer</th>
                    <th className="text-left font-semibold py-3 hidden md:table-cell">Method</th>
                    <th className="text-left font-semibold py-3 hidden md:table-cell">Date</th>
                    <th className="text-right font-semibold py-3">Total</th>
                    <th className="text-right font-semibold py-3"></th>
                  </tr></thead>
                  <tbody>
                    {invoices.map((inv) => (
                      <tr key={inv.id} className="border-b border-border/60 hover:bg-secondary/40 transition-colors">
                        <td className="py-3 font-medium">{inv.invoice_number}</td>
                        <td className="py-3">{inv.customer_name}</td>
                        <td className="py-3 hidden md:table-cell"><Badge variant="secondary" className="uppercase text-[10px]">{inv.payment_method}</Badge></td>
                        <td className="py-3 hidden md:table-cell text-muted-foreground">{timeAgo(inv.created_at)}</td>
                        <td className="py-3 text-right font-semibold tabular-nums">{formatINR(inv.total)}</td>
                        <td className="py-3 text-right"><Button size="sm" variant="ghost" onClick={() => setViewInvoice(inv)} data-testid={`view-invoice-${inv.id}`}>View</Button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        </TabsContent>
      </Tabs>

      {/* Invoice view */}
      <Dialog open={!!viewInvoice} onOpenChange={(o) => !o && setViewInvoice(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Invoice {viewInvoice?.invoice_number}</DialogTitle></DialogHeader>
          {viewInvoice && (
            <div className="text-sm">
              <div className="flex justify-between text-muted-foreground text-xs mb-4">
                <span>{viewInvoice.customer_name}</span>
                <span>{formatDateTime(viewInvoice.created_at)}</span>
              </div>
              <div className="space-y-2 border-y border-border py-3">
                {viewInvoice.items?.map((it, i) => (
                  <div key={i} className="flex justify-between">
                    <span>{it.name} × {it.quantity}</span>
                    <span className="tabular-nums">{formatINR(it.price * it.quantity - (it.discount || 0))}</span>
                  </div>
                ))}
              </div>
              <div className="space-y-1 pt-3">
                <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span>{formatINR(viewInvoice.subtotal)}</span></div>
                <div className="flex justify-between text-muted-foreground"><span>Discount</span><span>-{formatINR(viewInvoice.discount_total)}</span></div>
                <div className="flex justify-between text-muted-foreground"><span>Tax</span><span>{formatINR(viewInvoice.tax)}</span></div>
                <div className="flex justify-between font-heading text-lg font-bold pt-1"><span>Total</span><span className="text-primary">{formatINR(viewInvoice.total)}</span></div>
                <Badge variant="secondary" className="uppercase mt-2">{viewInvoice.payment_method} · {viewInvoice.payment_status}</Badge>
              </div>
              <Button variant="outline" onClick={() => window.print()} className="w-full mt-4"><Printer className="h-4 w-4 mr-2" /> Print</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
